import React from 'react';
import {
  GridColDef,
  GridRowsProp,
  GridAlignment,
  GridRowModel,
  GridEditInputCell,
  GridCellParams,
  GridPreProcessEditCellProps,
} from '@mui/x-data-grid';
import tokens from '@/schedules/tokens.json';
import { isNonTrueValue } from '@/employee-details/utils/employeeDetailsUtils';
import { HEADER_LABEL_WITH_SPACE } from '@/components/DataGrid/EpDataGridHOC';
import {
  ScheduleMonthType,
  ScheduleResponseItemShape,
  SchedulesSummariesResponseItemShape,
} from '@/schedules/services/schedulesApi';

const MIN_WIDTH_6DIGITS = 85;
const MIN_WIDTH_4DIGITS = 68;
const MIN_WIDTH_4CHARS = 120;
const MAX_WIDTH_ALL_COLUMNS = 185;

export interface ScheduleGridMonthsType {
  september: number;
  october: number;
  november: number;
  december: number;
  january: number;
  february: number;
  march: number;
  april: number;
  may: number;
  june: number;
  july: number;
  august: number;
}

export interface ScheduleGridRowType extends ScheduleGridMonthsType {
  id: number;
  startYear: number;
  name: string;
  daysInYear: number;
  insertionTimestamp: string;
  insertionUser: string;
}

export const MONTHS = [
  { sequenceId: '09', value: 'september' },
  { sequenceId: '10', value: 'october' },
  { sequenceId: '11', value: 'november' },
  { sequenceId: '12', value: 'december' },
  { sequenceId: '01', value: 'january' },
  { sequenceId: '02', value: 'february' },
  { sequenceId: '03', value: 'march' },
  { sequenceId: '04', value: 'april' },
  { sequenceId: '05', value: 'may' },
  { sequenceId: '06', value: 'june' },
  { sequenceId: '07', value: 'july' },
  { sequenceId: '08', value: 'august' },
];

export function scheduleMonthsDataFormatter(months: ScheduleMonthType[]) {
  const fomattedMonthsData: { [key: string]: number } = {};
  MONTHS.forEach(({ sequenceId, value }) => {
    fomattedMonthsData[value] =
      months.find(
        ({ employmentMonth }) =>
          employmentMonth?.substring(employmentMonth.length - 2) === sequenceId,
      )?.days || 0;
  });
  return fomattedMonthsData;
}

export function getRawSchedulesGridRowData(): ScheduleGridRowType {
  const rawSchedulesGridMonthsData = MONTHS.reduce((accu, { value }) => {
    accu[value as keyof ScheduleGridMonthsType] = 0;
    return accu;
  }, {} as ScheduleGridMonthsType);

  const rawScheduleGridRowData = {
    id: Math.floor(Math.random() * 90000) + 10000,
    name: '',
    isNew: true,
    startYear: new Date().getFullYear(),
    daysInYear: 0,
    insertionTimestamp: '',
    insertionUser: '',
    ...rawSchedulesGridMonthsData,
  };
  return rawScheduleGridRowData;
}

export function formatScheduleData(row: GridRowModel & ScheduleGridRowType) {
  const {
    startYear,
    name,
    september,
    october,
    november,
    december,
    january,
    february,
    march,
    april,
    may,
    june,
    july,
    august,
  } = row;

  const monthsTotal =
    september +
    october +
    november +
    december +
    january +
    february +
    march +
    april +
    may +
    june +
    july +
    august;
  const months = MONTHS.map(({ sequenceId, value }) => {
    return {
      employmentMonth: `${+sequenceId > 8 ? startYear : startYear + 1}${sequenceId}`,
      days: row[value],
    };
  });
  return {
    startYear,
    name,
    daysInYear: Math.round((monthsTotal + Number.EPSILON) * 10000) / 10000,
    months,
  };
}

export function dataGridRowBuilder(values: ScheduleResponseItemShape[]) {
  const rows: GridRowsProp & ScheduleGridRowType[] = values.map(
    (schedule: ScheduleResponseItemShape) => {
      const {
        scheduleId,
        startYear,
        name,
        daysInYear,
        months,
        editable,
        insertionTimestamp,
        insertionUser,
      } = schedule;
      const {
        september,
        october,
        november,
        december,
        january,
        february,
        march,
        april,
        may,
        june,
        july,
        august,
      } = scheduleMonthsDataFormatter(months);
      return {
        id: scheduleId,
        startYear,
        name,
        daysInYear,
        september,
        october,
        november,
        december,
        january,
        february,
        march,
        april,
        may,
        june,
        july,
        august,
        editable,
        insertionUser,
        insertionTimestamp,
      };
    },
  );

  return [...rows];
}

const handleFocus = (
  event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  params: GridCellParams,
) => {
  if (params.value === 0) {
    event.target.select();
  }
};

export function dataGridColumnBuilder() {
  const { GRID_LABELS, GRID_DESCRIPTION, GRID_ERROR } = tokens;
  const columns: GridColDef[] = [
    {
      field: 'startYear',
      headerName: GRID_LABELS.SCHOOL_YEAR,
      description: GRID_DESCRIPTION.SCHOOL_YEAR,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      cellClassName: 'ep-grid-highlighted-columns',
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            min: 1900,
            max: 3000,
            'aria-label': GRID_ERROR.SCHOOL_YEAR,
          }}
        />
      ),
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 3000 || params.props.value < 1900;
        return {
          ...params.props,
          error: !params.props.value || hasError,
          title: GRID_ERROR.SCHOOL_YEAR,
        };
      },
    },
    {
      field: 'name',
      headerName: GRID_LABELS.NAME,
      description: GRID_DESCRIPTION.NAME,
      type: 'string',
      minWidth: MIN_WIDTH_4CHARS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      editable: true,
      cellClassName: 'ep-grid-highlighted-columns',
      maxWidth: 400,
      flex: 1,
      preProcessEditCellProps: (params) => {
        const hasError = !params.props.value;
        return {
          ...params.props,
          error: hasError,
          title: GRID_ERROR.NAME,
        };
      },
      renderEditCell: (params) => {
        return (
          <GridEditInputCell
            {...params}
            inputProps={{
              'aria-label': GRID_LABELS.NAME,
            }}
          />
        );
      },
    },
    {
      field: 'daysInYear',
      headerName: GRID_LABELS.DAYS_IN_YEAR,
      description: GRID_DESCRIPTION.DAYS_IN_YEAR,
      type: 'number',
      minWidth: MIN_WIDTH_6DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        const total =
          params.row.september +
          params.row.october +
          params.row.november +
          params.row.december +
          params.row.january +
          params.row.february +
          params.row.march +
          params.row.april +
          params.row.may +
          params.row.june +
          params.row.july +
          params.row.august;
        return (
          <GridEditInputCell
            {...params}
            inputProps={{
              value: Math.round((total + Number.EPSILON) * 10000) / 10000,
              readOnly: true,
              'aria-label': GRID_LABELS.DAYS_IN_YEAR,
            }}
          />
        );
      },
      preProcessEditCellProps: async (params: GridPreProcessEditCellProps) => {
        const total =
          params.otherFieldsProps?.september?.value +
          params.otherFieldsProps?.october?.value +
          params.otherFieldsProps?.november?.value +
          params.otherFieldsProps?.december?.value +
          params.otherFieldsProps?.january?.value +
          params.otherFieldsProps?.february?.value +
          params.otherFieldsProps?.march?.value +
          params.otherFieldsProps?.april?.value +
          params.otherFieldsProps?.may?.value +
          params.otherFieldsProps?.june?.value +
          params.otherFieldsProps?.july?.value +
          params.otherFieldsProps?.august?.value;
        const hasError = total > 366 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
          title: 'Value must be less than 366 and greater than equal to 0',
        };
      },
      valueGetter: (val, row) => {
        const total =
          row.september +
          row.october +
          row.november +
          row.december +
          row.january +
          row.february +
          row.march +
          row.april +
          row.may +
          row.june +
          row.july +
          row.august;
        return Math.round((total + Number.EPSILON) * 10000) / 10000;
      },
      cellClassName: 'ep-grid-highlighted-columns',
    },
    {
      field: 'september',
      headerName: GRID_LABELS.SEPTEMBER,
      description: GRID_DESCRIPTION.SEPTEMBER,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.SEPTEMBER;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 30,
              'aria-label': GRID_LABELS.SEPTEMBER,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 30 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'october',
      headerName: GRID_LABELS.OCTOBER,
      description: GRID_DESCRIPTION.OCTOBER,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.OCTOBER;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 31,
              'aria-label': GRID_LABELS.OCTOBER,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 31 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'november',
      headerName: GRID_LABELS.NOVEMBER,
      description: GRID_DESCRIPTION.NOVEMBER,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.NOVEMBER;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 30,
              'aria-label': GRID_LABELS.NOVEMBER,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 30 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'december',
      headerName: GRID_LABELS.DECEMBER,
      description: GRID_DESCRIPTION.DECEMBER,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.DECEMBER;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 31,
              'aria-label': GRID_LABELS.DECEMBER,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 31 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'january',
      headerName: GRID_LABELS.JANUARY,
      description: GRID_DESCRIPTION.JANUARY,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.JANUARY;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 31,
              'aria-label': GRID_LABELS.JANUARY,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 31 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'february',
      headerName: GRID_LABELS.FEBRUARY,
      description: GRID_DESCRIPTION.FEBRUARY,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.tilte = GRID_ERROR.FEBRUARY;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 29,
              'aria-label': GRID_LABELS.FEBRUARY,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 29 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'march',
      headerName: GRID_LABELS.MARCH,
      description: GRID_DESCRIPTION.MARCH,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.MARCH;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 31,
              'aria-label': GRID_LABELS.MARCH,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 31 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'april',
      headerName: GRID_LABELS.APRIL,
      description: GRID_DESCRIPTION.APRIL,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.APRIL;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 30,
              'aria-label': GRID_LABELS.APRIL,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 30 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'may',
      headerName: GRID_LABELS.MAY,
      description: GRID_DESCRIPTION.MAY,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.MAY;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 31,
              'aria-label': GRID_LABELS.MAY,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 31 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'june',
      headerName: GRID_LABELS.JUNE,
      description: GRID_DESCRIPTION.JUNE,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.JUNE;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 30,
              'aria-label': GRID_LABELS.JUNE,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 30 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'july',
      headerName: GRID_LABELS.JULY,
      description: GRID_DESCRIPTION.JULY,
      type: 'number',
      minWidth: MIN_WIDTH_4DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.JULY;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 31,
              'aria-label': GRID_LABELS.JULY,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 31 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
    {
      field: 'august',
      headerName: GRID_LABELS.AUGUST,
      description: GRID_DESCRIPTION.AUGUST,
      type: 'number',
      minWidth: MIN_WIDTH_6DIGITS,
      valueFormatter: (value) => {
        return isNonTrueValue(value) ? '--' : value;
      },
      sortComparator: (v1, v2) => {
        return numericComparator(v1, v2);
      },
      editable: true,
      renderEditCell: (params) => {
        params.title = GRID_ERROR.AUGUST;
        return (
          <GridEditInputCell
            {...params}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleFocus(event, params)
            }
            inputProps={{
              min: 0,
              max: 31,
              'aria-label': GRID_LABELS.AUGUST,
            }}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value > 31 || params.props.value < 0;
        return {
          ...params.props,
          error: params.props.value === null || hasError,
        };
      },
    },
  ];

  return applyToAllColumns(columns);
}

function applyToAllColumns(columns: GridColDef[]) {
  return columns.map((column) => {
    return {
      ...column,
      maxWidth: column.maxWidth ?? MAX_WIDTH_ALL_COLUMNS,
      align: 'left' as GridAlignment,
      headerAlign: 'left' as GridAlignment,
      headerClassName: HEADER_LABEL_WITH_SPACE,
    };
  });
}

function numericComparator(a: number | string, b: number | string) {
  if (a === null || a === '' || (typeof a === 'string' && a.trim() === '')) {
    return -1;
  }
  if (b === null || b === '' || (typeof b === 'string' && b.trim() === '')) {
    return 1;
  }

  return Number(a) - Number(b);
}

export function deduplicateSchedulesResponse(schedules: SchedulesSummariesResponseItemShape[]) {
  const yearTracker: number[] = [];

  const filtered = schedules.filter((schedule) => {
    const startYear = schedule.startYear;
    if (!yearTracker.includes(startYear)) {
      yearTracker.push(startYear);
      return true;
    }
    return false;
  });

  return filtered;
}

export function sortScheduleResponseByYear(schedules: SchedulesSummariesResponseItemShape[]) {
  return [...schedules].sort((left, right) => right.startYear - left.startYear);
}
